'use client';

import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
}

let toastListener: ((toast: ToastMessage) => void) | null = null;

export function showToast(type: ToastType, message: string) {
  if (toastListener) {
    toastListener({ id: `toast-${type}-${message.slice(0, 8)}-${Date.now().toString().slice(-4)}`, type, message });
  }
}

const icons = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const toastStyles: Record<ToastType, { bg: string; color: string; border: string }> = {
  success: { bg: 'rgba(16, 185, 129, 0.15)', color: 'var(--success)', border: 'rgba(16, 185, 129, 0.3)' },
  error: { bg: 'rgba(239, 68, 68, 0.15)', color: 'var(--danger)', border: 'rgba(239, 68, 68, 0.3)' },
  warning: { bg: 'rgba(245, 158, 11, 0.15)', color: 'var(--warning)', border: 'rgba(245, 158, 11, 0.3)' },
  info: { bg: 'rgba(59, 130, 246, 0.15)', color: 'var(--info)', border: 'rgba(59, 130, 246, 0.3)' },
};

export default function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    toastListener = (toast) => {
      setToasts((prev) => [...prev, toast]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
      }, 3500);
    };
    return () => { toastListener = null; };
  }, []);

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((toast) => {
        const Icon = icons[toast.type];
        const style = toastStyles[toast.type];
        return (
          <div
            key={toast.id}
            className="flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl pointer-events-auto animate-slide-up"
            style={{ backgroundColor: style.bg, border: `1px solid ${style.border}`, backdropFilter: 'blur(8px)', minWidth: '280px', maxWidth: '380px' }}
          >
            <Icon size={16} style={{ color: style.color, flexShrink: 0 }} />
            <p className="text-sm flex-1" style={{ color: 'var(--foreground)' }}>{toast.message}</p>
            <button
              onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
              className="p-0.5 rounded hover:opacity-70"
              style={{ color: 'var(--muted-foreground)' }}
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
    </div>
  );
}